import { PromoConditionsRow } from 'features/PromoConditions/components';
import { PromoConditionsListProps } from 'features/PromoConditions/PromoConditionsAccordion.types';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'shared/util/utility';

const PromoConditionsListComponent = ({ selectedData, conditions, validation, events, categoryMap }: PromoConditionsListProps) => {
	const { t } = useTranslation();

	const prepareMinMaxFormat = (minimum?: number, maximum?: number, isStake = false, shouldFormat = true) => {
		const formatMin = (value?: number): string | null => {
			if (value == null) return null;
			if (!shouldFormat) return `${value}`;
			return isStake ? `${value}` : value.toFixed(2);
		};

		const formatMax = (value?: number): string | null => {
			if (value == null) return null;
			if (!shouldFormat) return `${value}`;
			return isStake ? `${value}${t('currency')}` : value.toFixed(2);
		};

		const formattedMin = formatMin(minimum);
		const formattedMax = formatMax(maximum);

		if (formattedMin && formattedMax) {
			return `${formattedMin} - ${formattedMax}`;
		}
		if (formattedMin) {
			return `${t('promoConditions_minimum')} ${formattedMin}`;
		}
		if (formattedMax) {
			return `${t('promoConditions_maximum')} ${formattedMax}`;
		}
		return null;
	};

	const mapValuesToString = (values: number[] | undefined): string => {
		if (!values) {
			return '';
		}
		return values
			.map((val) => {
				if (val === 0) return 'Ako';
				if (val === 100) return 'Solo';
				return '';
			})
			.join(', ');
	};

	const conditionsRows = useMemo(
		() => [
			// --- allIn ---
			{
				key: 'allInCategory',
				type: 'categoryIds',
				title: 'promoConditions_onlyOutOf',
				isComplete: !!validation.isAllInCategoryIdValid,
				iconType: 'dots',
				category: conditions.allIn.categoryIds,
			},
			{
				key: 'allInEvents',
				type: 'eventIds',
				title: 'promoConditions_event',
				isComplete: !!validation.isAllInEventIdValid,
				iconType: 'dots',
				category: conditions.allIn.eventIds,
				eventsList: events?.allInEvents,
			},
			{
				key: 'allInGameTypes',
				type: 'gameTypes',
				title: 'promoConditions_betType',
				isComplete: !!validation.isAllInGameTypeValid,
				iconType: 'dart',
				category: conditions.allIn.gameTypes,
			},
			{
				key: 'allInEventTypes',
				type: 'eventTypes',
				title: 'promoConditions_eventStatus',
				isComplete: !!validation.isAllInEventTypeValid,
				iconType: 'satelite',
				category: conditions.allIn.eventTypes,
			},
			// --- oneIn ---

			{
				key: 'oneInCategory',
				type: 'categoryIds',
				title: 'promoConditions_oneOutOf',
				isComplete: !!validation.isOneInCategoryValid,
				iconType: 'dots',
				category: conditions.oneIn.categoryIds,
			},
			{
				key: 'oneInEventTypes',
				type: 'eventTypes',
				title: 'promoConditions_eventTypeOneOutOf',
				isComplete: !!validation.isOneInEventTypeValid,
				iconType: 'satelite',
				category: conditions.oneIn.eventTypes,
			},
			{
				key: 'oneInGameTypes',
				type: 'gameTypes',
				title: 'promoConditions_betTypeOneOutOf',
				isComplete: !!validation.isOneInGameTypeValid,
				iconType: 'dart',
				category: conditions.oneIn.gameTypes,
			},
			// --- noneIn ---
			{
				key: 'noneInCategory',
				type: 'categoryIds',
				title: 'promoConditions_doesNotApply',
				isComplete: !!validation.isNoneInCategoryValid,
				iconType: 'warning',
				category: conditions?.noneIn.categoryIds,
			},
			{
				key: 'noneInEventIds',
				type: 'eventIds',
				title: 'promoConditions_doesNotApply',
				isComplete: !!validation.isNoneInEventIdValid,
				iconType: 'warning',
				category: conditions.noneIn.eventIds,
				eventsList: events?.noneInEvents,
			},
			{
				key: 'noneInEventTypes',
				type: 'eventIds',
				title: 'promoConditions_doesNotApply',
				isComplete: !!validation.isNoneInEventTypeValid,
				iconType: 'warning',
				category: conditions.noneIn.eventTypes,
			},
			{
				key: 'noneInGameTypes',
				type: 'gameTypes',
				title: 'promoConditions_doesNotApply',
				isComplete: !!validation.isNoneInGameTypeValid,
				iconType: 'warning',
				category: conditions.noneIn.gameTypes,
			},
			// --- odds ---
			{
				key: 'couponOdds',
				type: 'couponOdds',
				title: 'promoConditions_couponOdds',
				isComplete: !!validation.isCouponOddsValid,
				iconType: 'graph',
				value: prepareMinMaxFormat(conditions.odds.minCoupon, conditions.odds.maxCoupon),
			},
			// --- odds ---
			{
				key: 'eventOdds',
				type: 'eventOdds',
				title: 'promoConditions_eventOdds',
				isComplete: !!validation.isEventOddsValid,
				iconType: 'list',
				value: prepareMinMaxFormat(conditions.odds.minEvent),
			},
			// --- stake ---
			{
				key: 'stake',
				type: 'stake',
				title: 'currentStack',
				isComplete: !!validation.isStakeValid,
				iconType: 'moneybag',
				value: !conditions.stake.min && !conditions.stake.max ? null : prepareMinMaxFormat(conditions.stake.min, conditions.stake.max, true),
			},
			// --- number of bets ---
			{
				key: 'numberOfBets',
				type: 'numberOfBets',
				title: 'minEvents',
				isComplete: !!validation.isNumberOfBetsValid,
				iconType: 'count',
				value: prepareMinMaxFormat(conditions.outcomes.min, conditions.outcomes.max, false, false),
			},
			// --- time ---
			{
				key: 'time',
				type: 'time',
				title: 'promoConditions_timeConditions',
				isComplete: !!validation.isTimeValid,
				iconType: 'calendar',
				value: selectedData.validTo && `${t('promoConditions_validTo')} ${formatDate(selectedData.validTo, 'DD.MM.YYYY HH:mm')}`,
			},
			{
				key: 'coupon',
				type: 'coupon',
				title: 'promoConditions_coupon',
				isComplete: !!validation.isCouponValid,
				iconType: 'admitone',
				value: mapValuesToString(conditions?.slipBetTypes),
			},
		],
		[events, validation, conditions, selectedData, t],
	);

	return (
		<ul className="flex flex-col gap-0.5 relative">
			{conditionsRows.map(({ key, title, type, isComplete, iconType, value, category, eventsList }) => {
				return (
					<PromoConditionsRow
						key={key}
						type={type}
						title={title}
						isComplete={isComplete}
						iconType={iconType}
						value={value}
						category={category}
						eventsList={eventsList}
						categoryMap={categoryMap}
					/>
				);
			})}
		</ul>
	);
};

export const PromoConditionsList = memo(PromoConditionsListComponent);
